import { useEffect, useRef, useState } from "react";
import { useClickOutside } from "../../lib/useClickOutside";
import { XIcon } from "./Icons";

/** Janela de datas "YYYY-MM-DD" (inclusivas), cada ponta opcional e independente.
 *  Calendário próprio (tokens do tema), popover fecha ao clicar fora / Esc. */
export interface DateRange { start: string | null; end: string | null }

const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const WEEK = ["D", "S", "T", "Q", "Q", "S", "S"];

export function fmtDay(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]}${y !== new Date().getFullYear() ? ` ${y}` : ""}`;
}
export function rangeLabel(r: DateRange): string {
  if (r.start && r.end) return `${fmtDay(r.start)} → ${fmtDay(r.end)}`;
  if (r.start) return `a partir de ${fmtDay(r.start)}`;
  if (r.end) return `até ${fmtDay(r.end)}`;
  return "sempre";
}
const toIso = (y: number, m: number, d: number) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const todayIso = () => { const t = new Date(); return toIso(t.getFullYear(), t.getMonth(), t.getDate()); };

export function DateRangePicker({ value, onChange, color, title }: {
  value: DateRange; onChange: (r: DateRange) => void; color?: string; title?: string;
}) {
  const [open, setOpen] = useState(false);
  const [field, setField] = useState<"start" | "end">("start");
  const anchor = value.start ?? value.end ?? todayIso();
  const [view, setView] = useState(() => ({ y: Number(anchor.slice(0, 4)), m: Number(anchor.slice(5, 7)) - 1 }));
  const ref = useRef<HTMLSpanElement>(null);

  useClickOutside(ref, open, () => setOpen(false));
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  function pick(iso: string) {
    if (field === "start") {
      onChange({ start: iso, end: value.end && value.end < iso ? null : value.end });
      setField("end");
    } else {
      onChange({ end: iso, start: value.start && value.start > iso ? null : value.start });
    }
  }
  const hasAny = !!(value.start || value.end);
  const first = new Date(view.y, view.m, 1).getDay();
  const days = new Date(view.y, view.m + 1, 0).getDate();
  const today = todayIso();
  const inRange = (iso: string) => hasAny && (!value.start || iso >= value.start) && (!value.end || iso <= value.end);

  const chip: React.CSSProperties = { background: "var(--surface-input)", border: `1px solid ${color ?? "var(--line-strong)"}`, borderRadius: 6, color: color ?? "var(--ink)", fontSize: 11, padding: "2px 8px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 };
  const fieldBtn = (k: "start" | "end", label: string) => (
    <button onClick={() => setField(k)} style={{ flex: 1, background: field === k ? "var(--brand-soft)" : "var(--surface-input)", border: `1px solid ${field === k ? "var(--brand-line)" : "var(--line)"}`, borderRadius: 6, padding: "6px 8px", cursor: "pointer", textAlign: "left", color: "var(--ink)" }}>
      <div style={{ fontSize: 10, color: "var(--ink-muted)" }}>{label}</div>
      <div style={{ fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {value[k] ? fmtDay(value[k]!) : <span style={{ color: "var(--ink-dim)" }}>{k === "start" ? "imediato" : "sem fim"}</span>}
        {value[k] && <span onClick={(e) => { e.stopPropagation(); onChange({ ...value, [k]: null }); }} title="Limpar" style={{ display: "flex", color: "var(--ink-soft)" }}><XIcon size={11} /></span>}
      </div>
    </button>
  );

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span title={title} style={chip} onClick={() => setOpen((o) => !o)}>
        {rangeLabel(value)}
        {hasAny && <span onClick={(e) => { e.stopPropagation(); onChange({ start: null, end: null }); }} title="Limpar datas" style={{ display: "flex" }}><XIcon size={11} /></span>}
      </span>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: "var(--z-dropdown)" as any, width: 248, background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 10, boxShadow: "var(--shadow-pop)", padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 6 }}>{fieldBtn("start", "Início")}{fieldBtn("end", "Fim")}</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "var(--ink)" }}>
            <button onClick={() => setView((v) => v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 })} style={navBtn}>‹</button>
            <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{MONTHS[view.m]} {view.y}</span>
            <button onClick={() => setView((v) => v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 })} style={navBtn}>›</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, fontSize: 11, textAlign: "center" }}>
            {WEEK.map((w, i) => <div key={i} style={{ color: "var(--ink-muted)", padding: 2 }}>{w}</div>)}
            {Array.from({ length: first }).map((_, i) => <div key={`e${i}`} />)}
            {Array.from({ length: days }).map((_, i) => {
              const iso = toIso(view.y, view.m, i + 1);
              const edge = iso === value.start || iso === value.end;
              return (
                <button key={iso} onClick={() => pick(iso)} style={{
                  padding: "4px 0", borderRadius: 5, cursor: "pointer", fontSize: 11,
                  border: iso === today ? "1px solid var(--brand-line)" : "1px solid transparent",
                  background: edge ? "var(--brand)" : inRange(iso) ? "var(--brand-soft)" : "transparent",
                  color: edge ? "#fff" : "var(--ink)",
                }}>{i + 1}</button>
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
            <button onClick={() => { onChange({ start: null, end: null }); setField("start"); }} disabled={!hasAny} style={{ ...navBtn, color: hasAny ? "var(--danger)" : "var(--ink-dim)", cursor: hasAny ? "pointer" : "default" }}>Limpar tudo</button>
            <button onClick={() => setOpen(false)} style={{ ...navBtn, color: "var(--brand)" }}>Fechar</button>
          </div>
        </div>
      )}
    </span>
  );
}
const navBtn: React.CSSProperties = { background: "none", border: "none", color: "var(--ink-soft)", cursor: "pointer", fontSize: 14, padding: "2px 6px", lineHeight: 1 };
